<template>
  <div>
    <div
      class="reset-container-padding mt-8 rounded-2xl bg-light-grey p-4 pb-8 pt-6 lg:mx-0 lg:p-6"
    >
      <div class="mb-10">
        <RadioGroup v-model="subscriptionChooserActive" name="purchase-selection">
          <Radio :value="false">
            <div class="flex flex-row font-medium">
              <CommonLabel
                path="BWF/shop/productDetailPage/subscriptionProduct/singlePurchaseLabel"
              />&nbsp;<SharedPrice :value="singlePurchasePrice" class="ml-auto" />
            </div>
          </Radio>
        </RadioGroup>

        <RevealComponent
          v-if="configurator?.length > 0 || hasBulkPrice"
          :open="!subscriptionChooserActive"
        >
          <div v-if="hasBulkPrice" class="space-y-2 pt-8">
            <SelectField
              v-model="productQuantity"
              :label="quantityLabel"
              :options="bulkChooserOptions"
            />
          </div>

          <div v-else-if="configurator?.length > 0" class="space-y-4 pt-8">
            <CommonLabel
              class="text-base font-medium"
              path="BWF/shop/productDetailPage/subscriptionProduct/packSizeLabel"
            />
            <ProductVariantConfigurator
              class="p-0.5"
              :option-button-default="optionButtonDefault"
              :show-option-name="false"
              @change="subscriptionChange"
              @before-change="$emit('beforeSubscriptionChange')"
            />
          </div>
        </RevealComponent>
      </div>

      <div class="relative rounded-2xl bg-white p-4 lg:p-6">
        <Tag
          v-if="activeSubscriptionOption?.subscriptionPrice?.displayDiscountRate"
          class="absolute right-8 top-0 shrink-0 -translate-y-1/2"
          color="highlight"
        >
          <CommonLabel path="BWF/shop/productDetailPage/subscriptionProduct/savingsBadgeLabel" />
          <FormattedPercent
            :precision="0"
            :value="activeSubscriptionOption?.subscriptionPrice?.displayDiscountRate"
          />
        </Tag>

        <div class="mb-8">
          <CommonLabel
            class="text-base font-medium"
            path="BWF/shop/productDetailPage/subscriptionProduct/recommendationLabel"
          />
        </div>

        <RadioGroup v-model="subscriptionChooserActive" name="product-benefits" class="mb-6">
          <Radio :value="true">
            <div>
              <div class="flex flex-row font-medium">
                <CommonLabel
                  path="BWF/shop/productDetailPage/subscriptionProduct/subscriptionLabel"
                />
                <SharedPrice
                  class="ml-auto"
                  discounted
                  :value="subscriptionTotalPrice"
                />&nbsp;<Tint
                  v-if="subscriptionListPrice"
                  :class="{ 'ml-auto': !subscriptionListPrice }"
                  ><SharedPrice class="font-medium" strikethrough :value="subscriptionListPrice"
                /></Tint>
              </div>
              <Tint class="subscription-option-benefits mt-2 text-sm" tag="div">
                <RichTextComponent
                  v-if="activeSubscriptionOption?.benefits"
                  :rich-text="activeSubscriptionOption?.benefits"
                />
              </Tint>
            </div>
          </Radio>
        </RadioGroup>

        <RevealComponent :open="subscriptionChooserActive">
          <div class="space-y-4">
            <SelectField
              v-model="activeSubscriptionIntervalId"
              :label="subscriptionIntervalLabel"
              :options="subscriptionChooserOptions"
              class="form-field-grey"
            />
            <Tint class="pb-6" tag="div">
              <RichTextComponent :rich-text="activeSubscriptionOption?.description" />
            </Tint>
          </div>
        </RevealComponent>
        <button
          v-if="hasSubscriptionModalArea"
          class="text-link text-start text-black"
          @click="subscriptionModal.open()"
        >
          <CommonLabel
            class="font-medium underline"
            path="BWF/shop/productDetailPage/subscriptionProduct/subscriptionModalLinkLabel"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Product } from "@shopware-pwa/types";
import { Radio } from "~/components/clarity-ui/Radio";
import { RadioGroup } from "~/components/clarity-ui/Radio/RadioGroup";
import { Tag } from "~/components/clarity-ui/Tag";
import { Tint } from "~/components/clarity-ui/Tint";
import { FormattedPercent } from "~/components/ui/FormattedPercent";
import { useProductBulkPrices } from "~/composables/shop/useProductBulkPrices";
import { useProductSubscriptionConfigurator } from "~/composables/shop/useProductSubscriptionConfigurator";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import RichTextComponent from "~/templates/components/cms/RichTextComponent.vue";
import RevealComponent from "~/templates/elements/RevealComponent.vue";
import SelectField from "~/templates/elements/form/SelectField.vue";

export interface ProductSubscriptionConfiguratorProps {
  optionButtonDefault?: boolean;
}

defineProps<ProductSubscriptionConfiguratorProps>();

const { product, configurator } = useProduct();
const { unitPrice } = useProductPrice(product);

const { hasBulkPrice, productQuantity, bulkChooserOptions, activeBulkChild } =
  useProductBulkPrices(product);

const singlePurchasePrice = computed(() => {
  if (hasBulkPrice.value) {
    return activeBulkChild.value?.totalPrice;
  } else {
    return unitPrice.value;
  }
});

// todo: 'subscriptionChooserActive' needs to be enhanced. It's a boolean which is used as a value
// for <Radio>, but the value can only be a string! It can be a more complex object where the selected
// radio is saved by name.
// (https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input/radio#value)
const {
  subscriptionChooserOptions,
  subscriptionChooserActive,
  activeSubscriptionOption,
  activeSubscriptionIntervalId,
  subscriptionTotalPrice,
  subscriptionListPrice,
} = useProductSubscriptionConfigurator(product);

const { getLabelByPath } = useCommonLabels();
const subscriptionIntervalLabel = getLabelByPath(
  "BWF/shop/productDetailPage/subscriptionProduct/subscriptionIntervalDropdownLabel",
);
const quantityLabel = getLabelByPath("BWF/shop/shared/tableQuantity");

const subscriptionModal = inject("subscriptionModal", useModal());
const hasSubscriptionModalArea = inject("hasSubscriptionModalArea");
const emit = defineEmits<{
  (e: "beforeSubscriptionChange"): void;
  (e: "change", changedProduct: Partial<Product>): void;
}>();

const subscriptionChange = (changedProduct: Product) => {
  emit("change", changedProduct);
};
</script>

<style>
.subscription-option-benefits {
  ul {
    list-style-image: url("~/assets/sprite/svg/Checkmark-1.svg");
  }

  li {
    @apply pl-1;
  }
}
</style>
